import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import VillageCanvas from './VillageCanvas';
import { Soundscape } from './audio';
import { StoryRuntime } from './storyRuntime';
import { ChapterRail, EndingChoices, JournalPanel, SourcesDialog, StoryReader } from './components/StoryPanels';
import ExploreOverlay from './components/ExploreOverlay';
import { itemDef } from './data/exploration';
import { InkChars, InkFade, InkTitle, screenVariants } from './components/InkReveal';
import type { SavedState, StoryData } from './types';

// Atmosphere layer is decorative and off the reading path; split it out of the
// initial bundle and let it fade in whenever the engine is ready.
const AtmosphereParticles = lazy(() => import('./components/AtmosphereParticles'));

const SAVE_KEY = 'guideng-lianyungang-save-v1';
const SCENE_FILES = [
  '01-hutong-entry', '02-alley-sign', '03-old-bookstall', '04-old-house',
  '05-red-white-procession-v2', '06-paper-shop', '07-pursuit-stone-alley-v2', '08-theatre',
  '09-old-cinema', '10-game-arcade', '11-huai-an-inn', '12-lantern-ending',
];
const PROP_FILES = ['old-letter', 'alley-sign', 'book-pages', 'family-photo', 'lantern', 'paper-house', 'mirror-fragment', 'lantern', 'film-frames', 'arcade-screen', 'door-rings', 'guestbook'];

function loadSave(): SavedState | undefined {
  try {
    const value = localStorage.getItem(SAVE_KEY);
    return value ? JSON.parse(value) as SavedState : undefined;
  } catch {
    return undefined;
  }
}

function App() {
  const [story, setStory] = useState<StoryData>();
  const [loadError, setLoadError] = useState('');
  const [currentId, setCurrentId] = useState(loadSave()?.currentId ?? 'intro');
  const [visited, setVisited] = useState<string[]>(loadSave()?.visited ?? []);
  const [clues, setClues] = useState<string[]>(loadSave()?.collected ?? []);
  const [choiceMemory, setChoiceMemory] = useState<Record<string, string>>(loadSave()?.choices ?? {});
  const [solvedPuzzles, setSolvedPuzzles] = useState<string[]>(loadSave()?.puzzles ?? []);
  const [endingId, setEndingId] = useState(loadSave()?.endingId);
  const [inventory, setInventory] = useState<string[]>(loadSave()?.inventory ?? []);
  const [consumedHotspots, setConsumedHotspots] = useState<string[]>(loadSave()?.consumedHotspots ?? []);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [divined, setDivined] = useState(false);
  const [started, setStarted] = useState(loadSave()?.currentId !== undefined && loadSave()?.currentId !== 'intro');
  const [explored, setExplored] = useState<string[]>(loadSave()?.explored ?? []);
  const [dialog, setDialog] = useState<'map' | 'journal' | 'sources' | 'settings' | null>(null);
  const [pendingSkipId, setPendingSkipId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [choice, setChoice] = useState('');
  const [answer, setAnswer] = useState('');
  const [soundOn, setSoundOn] = useState(false);
  const [volume, setVolume] = useState(0.55);
  const [inkLines, setInkLines] = useState<string[]>([]);
  const [storyPage, setStoryPage] = useState(0);
  const [inkChoices, setInkChoices] = useState<Array<{ index: number; text: string }>>([]);
  const runtime = useRef<StoryRuntime | undefined>(undefined);
  const sound = useRef(new Soundscape());
  const dialogPanel = useRef<HTMLElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  const locations = useMemo(() => story ? story.chapters : [], [story]);
  const pageIndex = locations.findIndex((chapter) => chapter.id === currentId);
  const chapter = pageIndex >= 0 ? locations[pageIndex] : undefined;
  const ending = story?.endings?.find((item) => item.id === endingId || item.knot === endingId);
  const intro = !started || currentId === 'intro';
  const collected = useMemo(() => new Set(clues), [clues]);
  const consumedHotspotSet = useMemo(() => new Set(consumedHotspots), [consumedHotspots]);
  const solvedPuzzleSet = useMemo(() => new Set(solvedPuzzles), [solvedPuzzles]);
  const puzzleSolved = chapter ? solvedPuzzles.includes(chapter.id) : false;

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch('/story/chapters.json').then((response) => {
        if (!response.ok) throw new Error('剧情章节数据不可用');
        return response.json() as Promise<StoryData>;
      }),
      fetch('/story/story.json').then((response) => {
        if (!response.ok) throw new Error('Ink 故事运行文件不可用');
        return response.text();
      }),
    ]).then(([data, ink]) => {
      if (cancelled) return;
      setStory(data);
      runtime.current = new StoryRuntime(ink);
      const saved = loadSave();
      const validIds = new Set(data.chapters.map((item) => item.id));
      const validEndings = new Set((data.endings ?? []).map((item) => item.id));
      if (saved?.currentId && (validIds.has(saved.currentId) || validEndings.has(saved.currentId))) {
        setCurrentId(saved.currentId);
        setStarted(true);
        setVisited((saved.visited ?? []).filter((id) => validIds.has(id)));
        const clueIds = new Set(data.chapters.map((item) => item.clue.id));
        setClues((saved.collected ?? []).filter((id) => clueIds.has(id)));
        const exploredIds = saved.explored ?? data.chapters.filter((item) => saved.collected?.includes(item.clue.id)).map((item) => item.id);
        setExplored(exploredIds.filter((id) => validIds.has(id)));
        setChoiceMemory(saved.choices ?? {});
        setSolvedPuzzles(saved.puzzles ?? []);
        setInventory(saved.inventory ?? []);
        setConsumedHotspots(saved.consumedHotspots ?? []);
        setEndingId(saved.endingId);
      } else {
        setCurrentId('intro');
        setStarted(false);
        setEndingId(undefined);
      }
    }).catch((error: unknown) => {
      if (!cancelled) setLoadError(error instanceof Error ? error.message : '无法载入故事资料');
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!story || !chapter || !runtime.current) return;
    try {
      const lines = runtime.current.readKnot(chapter.knot ?? chapter.id);
      const choices = runtime.current.choices().map(({ index, text }) => ({ index, text }));
      setInkLines(lines.length ? lines : chapter.prose);
      setInkChoices(choices);
    } catch {
      setInkLines(chapter.prose);
      setInkChoices([]);
    }
    setAnswer('');
    setChoice('');
    setStoryPage(0);
    setMessage('');
    sound.current.setScene(Math.max(0, pageIndex));
  }, [story, currentId, chapter, pageIndex]);

  useEffect(() => {
    if (!dialog) {
      returnFocus.current?.focus();
      returnFocus.current = null;
      return;
    }

    if (!returnFocus.current && document.activeElement instanceof HTMLElement) {
      returnFocus.current = document.activeElement;
    }
    const panel = dialogPanel.current;
    if (!panel) return;
    const getFocusable = () => Array.from(panel.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.getClientRects().length > 0);
    const initialFocus = requestAnimationFrame(() => getFocusable()[0]?.focus());
    const containTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', containTab);
    return () => {
      cancelAnimationFrame(initialFocus);
      document.removeEventListener('keydown', containTab);
    };
  }, [dialog]);

  useEffect(() => {
    if (chapter) setChoice(choiceMemory[chapter.id] ?? '');
  }, [chapter, choiceMemory]);

  useEffect(() => {
    if (!story || intro) return;
    const data: SavedState = { currentId, collected: clues, visited, choices: choiceMemory, explored, puzzles: solvedPuzzles, endingId, inventory, consumedHotspots };
    try { localStorage.setItem(SAVE_KEY, JSON.stringify(data)); } catch { /* The story remains playable if storage is unavailable. */ }
  }, [story, intro, currentId, clues, visited, choiceMemory, explored, solvedPuzzles, endingId, inventory, consumedHotspots]);

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(''), 5000);
    return () => window.clearTimeout(timer);
  }, [message]);

  const goTo = useCallback((id: string) => {
    setCurrentId(id);
    setStarted(true);
    setEndingId(undefined);
    setDialog(null);
    setPendingSkipId(null);
    setVisited((old) => old.includes(id) ? old : [...old, id]);
    setMessage('');
  }, []);

  // Soft gate for the station-11 name-check puzzle: jumping forward past the
  // unsolved puzzle (via map, rail dots, nav buttons or keyboard) shows a
  // non-blocking confirmation instead of hard-locking the route.
  const guardedGoTo = useCallback((id: string) => {
    const targetIndex = locations.findIndex((item) => item.id === id);
    if (pageIndex === 10 && !puzzleSolved && targetIndex > pageIndex) {
      setDialog(null);
      setPendingSkipId(id);
      return;
    }
    goTo(id);
  }, [goTo, locations, pageIndex, puzzleSolved]);

  const startJourney = useCallback(async () => {
    if (!story?.chapters.length) return;
    if (!soundOn) {
      try {
        await sound.current.start();
        sound.current.setVolume(volume * 0.3);
        setSoundOn(sound.current.isEnabled);
      } catch { setMessage('浏览器没有开放声音；你仍可以继续阅读，声音提示也会显示在画面上。'); }
    }
    goTo(story.chapters[0].id);
  }, [story, soundOn, volume, goTo]);

  const toggleSound = async () => {
    if (soundOn) {
      sound.current.stop();
      setSoundOn(false);
      return;
    }
    try {
      await sound.current.start();
      sound.current.setVolume(volume * 0.3);
      sound.current.setScene(Math.max(0, pageIndex));
      setSoundOn(sound.current.isEnabled);
    } catch { setMessage('浏览器没有开放声音。'); }
  };

  const updateVolume = (next: number) => {
    setVolume(next);
    sound.current.setVolume(next * 0.3);
  };

  const inspectScene = () => {
    if (!chapter) return;
    if (inkChoices.length > 1 && pageIndex !== locations.length - 1 && !choice) {
      setMessage('先选一个行动，再继续前进。');
      return;
    }
    if (pageIndex === 10 && !puzzleSolved) {
      const cleaned = answer.replace(/[\s、,，；;。]/g, '');
      const expected = (chapter.puzzle?.answer ?? '').replace(/[\s、,，；;。]/g, '');
      const isCorrect = expected.length > 0 && cleaned === expected;
      if (!isCorrect) {
        setMessage(chapter.puzzle?.hint ?? '黑白差役仍没有落笔：两个姓名，各自写回各自一栏。');
        sound.current.cue('knock');
        return;
      }
      setSolvedPuzzles((old) => old.includes(chapter.id) ? old : [...old, chapter.id]);
    }
    if (!explored.includes(chapter.id)) {
      const clue = chapter.clue;
      setClues((old) => old.includes(clue.id) ? old : [...old, clue.id]);
      setExplored((old) => [...old, chapter.id]);
      if (runtime.current && inkChoices.length) {
        try { runtime.current.choose(0); } catch { /* exported chapter text remains the fallback */ }
      }
      sound.current.cue(pageIndex === 6 ? 'footstep' : 'paper');
      setMessage(`线索已记入手记：${clue.title}`);
    } else {
      setMessage('你已经看过这里。线索仍留在手记里。');
    }
  };

  const chooseInkAction = (index: number) => {
    if (!chapter) return;
    const selected = inkChoices[index];
    if (!selected) return;
    if (pageIndex === locations.length - 1 && story?.endings?.[index]) {
      chooseEnding(story.endings[index].id);
      return;
    }
    setChoice(selected.text);
    setChoiceMemory((old) => ({ ...old, [chapter.id]: selected.text }));
    setInkChoices([]);
    const outcome = (() => {
      try { return runtime.current?.choose(selected.index) ?? []; } catch { return []; }
    })();
    if (!explored.includes(chapter.id)) {
      setClues((old) => old.includes(chapter.clue.id) ? old : [...old, chapter.clue.id]);
      setExplored((old) => [...old, chapter.id]);
      setMessage(`${outcome[0] ?? selected.text} · 线索已记入手记：${chapter.clue.title}`);
    } else {
      setMessage(outcome[0] ?? selected.text);
    }
    sound.current.cue(pageIndex === 6 ? 'footstep' : pageIndex === 2 ? 'knock' : pageIndex === 7 ? 'whisper' : 'paper');
  };

  const moveNext = useCallback(async () => {
    if (!chapter) return;
    if (pageIndex === locations.length - 1) {
      setDialog('sources');
      setMessage('终章的选择已写进客簿。请从结局卡选择这盏灯的去向。');
      return;
    }
    const next = locations[pageIndex + 1];
    setVisited((old) => old.includes(next.id) ? old : [...old, next.id]);
    guardedGoTo(next.id);
  }, [chapter, pageIndex, locations, guardedGoTo]);

  const chooseEnding = (id: string) => {
    const picked = story?.endings?.find((item) => item.id === id || item.knot === id)
      ?? story?.endings?.[0];
    if (!picked) return;
    const pickedIndex = story?.endings?.indexOf(picked) ?? 0;
    if (runtime.current && inkChoices.length > pickedIndex) {
      try { runtime.current.choose(inkChoices[pickedIndex].index); } catch { /* Keep the authored ending available. */ }
    }
    try { if (runtime.current) setInkLines(runtime.current.readKnot(picked.knot ?? picked.id)); } catch { /* Use the exported novel text. */ }
    setStoryPage(0);
    setEndingId(picked.id);
    setCurrentId(picked.id);
    setDialog(null);
    setMessage('');
    sound.current.cue('sting');
  };

  const newGame = () => {
    localStorage.removeItem(SAVE_KEY);
    sound.current.stop();
    setSoundOn(false);
    setStarted(false);
    setCurrentId('intro');
    setVisited([]);
    setClues([]);
    setChoiceMemory({});
    setSolvedPuzzles([]);
    setExplored([]);
    setInventory([]);
    setConsumedHotspots([]);
    setDivined(false);
    setExploreOpen(false);
    setEndingId(undefined);
    setDialog(null);
  };

  // ---- Phase 3: exploration + inventory + puzzles ----

  const handlePickup = useCallback((itemId: string, hotspotId: string) => {
    setInventory((old) => old.includes(itemId) ? old : [...old, itemId]);
    setConsumedHotspots((old) => old.includes(hotspotId) ? old : [...old, hotspotId]);
    sound.current.cue('paper');
  }, []);

  const handleCombine = useCallback((consumedIds: string[], gained: string, _text: string) => {
    setInventory((old) => [...old.filter((id) => !consumedIds.includes(id)), gained]);
    sound.current.cue('paper');
  }, []);

  const handleUseItemOnHotspot = useCallback((itemId: string, hotspot: { id: string; acceptsItem?: string; useItemText?: string; wrongItemText?: string }) => {
    const accepted = hotspot.acceptsItem === itemId || itemDef(itemId)?.usableOn?.includes(hotspot.id);
    if (accepted) {
      // S5: offering wine at the altar solves the divination puzzle.
      if (hotspot.id === 's05-altar') {
        setSolvedPuzzles((old) => old.includes('divination-kun') ? old : [...old, 'divination-kun']);
        setConsumedHotspots((old) => old.includes(hotspot.id) ? old : [...old, hotspot.id]);
        // The wine is poured out; the vessel stays as a used offering.
        setInventory((old) => old.filter((id) => id !== itemId));
        sound.current.cue('sting');
        return hotspot.useItemText ?? '酒入盏中。';
      }
      setConsumedHotspots((old) => old.includes(hotspot.id) ? old : [...old, hotspot.id]);
      return hotspot.useItemText ?? '用上了。';
    }
    sound.current.cue('knock');
    return hotspot.wrongItemText ?? '这东西用在这里不合适。';
  }, []);

  const handlePuzzleSolved = useCallback((puzzleId: string, _text: string) => {
    setSolvedPuzzles((old) => old.includes(puzzleId) ? old : [...old, puzzleId]);
    sound.current.cue('sting');
  }, []);

  const handleDivined = useCallback(() => setDivined(true), []);

  const branchEcho = pageIndex === 3 && choiceMemory['station-03']
    ? choiceMemory['station-03'].includes('按住')
      ? '笔尖被按住后仍越过竖线；那一抹蓝墨留在你的指腹。'
      : '你松开笔杆后，桌底多出的敲击一路跟到了院门。'
    : pageIndex === 7 && choiceMemory['station-07']
      ? choiceMemory['station-07'].includes('藏身')
        ? '你曾躲进侧门；挂布外留下的湿手印没有追进来。'
        : '你曾从尸身手臂下穿出；湿脚印一路跟到了戏台侧门。'
      : pageIndex === 10 && choiceMemory['station-10']
        ? choiceMemory['station-10'].includes('左侧')
          ? '你沿左侧石阶数过回环；旧钥匙的齿缝里仍有盐粒。'
          : '你跟随白灯穿过仓门；旧钥匙像从屏幕另一侧递来。'
        : '';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setDialog(null); return; }
      // The exploration overlay has its own keyboard model (Tab/Enter/arrows
      // move between hotspots); don't hijack keys while it's open.
      if (dialog || intro || ending || !chapter || exploreOpen) return;
      // Never hijack typing inside form fields (e.g. the station-11 puzzle input).
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.key === 'ArrowRight' && pageIndex < locations.length - 1) void moveNext();
      if (event.key === 'ArrowLeft' && pageIndex > 0) guardedGoTo(locations[pageIndex - 1].id);
      if (event.key.toLowerCase() === 'j') setDialog('journal');
      if (event.key.toLowerCase() === 'm') setDialog('map');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialog, intro, ending, chapter, pageIndex, locations, goTo, moveNext, guardedGoTo, exploreOpen]);

  // Hooks must stay above the early returns below: on the first render the
  // story has not loaded yet, and changing the hook count between renders
  // crashes React (minified error #310) leaving a black screen.
  const chapterLabels = useMemo(() => locations.map((item, index) => `第 ${String(index + 1).padStart(2, '0')} 站：${item.title.replace(/^\d+[｜|.、]\s*/, '')}`), [locations]);

  // Preload the current and next chapter's scene images so switching chapters
  // does not flash an empty backdrop.
  useEffect(() => {
    [pageIndex, pageIndex + 1].forEach((index) => {
      const file = SCENE_FILES[index];
      if (!file) return;
      const image = new Image();
      image.src = `/assets/scenes/${file}.webp`;
    });
  }, [pageIndex]);

  if (loadError) return <main className="fatal-state"><div className="seal">归</div><p>故事暂时没有接上。</p><small>{loadError} · 请重新构建项目</small></main>;
  if (!story) return <main className="loading-state"><span className="loading-lamp" /><p>灯还没有亮</p><small>正在翻开客簿……</small></main>;

  const sceneImage = ending ? SCENE_FILES[11] : SCENE_FILES[Math.max(0, pageIndex)];

  return (
    <MotionConfig reducedMotion="user">
    <main className={`app-shell ${intro ? 'is-intro' : ''} ${ending ? 'is-ending' : ''}`}>
      <div className="grain" aria-hidden="true" />
      <div className="scene-backdrop" style={{ backgroundImage: `url(/assets/scenes/${sceneImage}.webp)` }} />
      <div className="scene-vignette" />
      <Suspense fallback={null}>
        <AtmosphereParticles />
      </Suspense>
      <header className="topbar">
        <button className="brand-lockup" type="button" onClick={() => intro ? undefined : setDialog('map')} aria-label="打开连云老街路线图">
          <span className="brand-seal">归</span><span><b>归灯</b><i>连云老街异闻录</i></span>
        </button>
        <div className="top-center"><span className="live-dot" />沿海旧街 · 雾夜 01:17</div>
        <div className="top-actions">
          <button className="icon-button sound-button" type="button" onClick={() => void toggleSound()} aria-label={soundOn ? '关闭环境声' : '开启环境声'} title={soundOn ? '关闭环境声' : '开启环境声'}>
            <span className={`sound-glyph ${soundOn ? 'is-playing' : ''}`}>{soundOn ? '♫' : '◖'}</span><span className="sound-label">{soundOn ? '声场开启' : '声音关闭'}</span>
          </button>
          <button className="icon-button settings-button" type="button" onClick={() => setDialog('settings')} aria-label="声音与无障碍设置" title="声音与无障碍设置">⚙</button>
          <button className="icon-button journal-button" type="button" onClick={() => setDialog('journal')} aria-label={`打开线索手记，已有 ${clues.length} 条`}>
            <span className="book-icon">▤</span><span className="sound-label">手记 <b>{String(clues.length).padStart(2, '0')}</b></span>
          </button>
          <button className="icon-button map-button" type="button" onClick={() => setDialog('map')} aria-label="打开街区模型与路线">街区 <span>⌖</span></button>
        </div>
      </header>

      <motion.div key={intro ? 'intro' : ending ? `ending-${ending.id}` : chapter?.id ?? 'none'} className="transition-veil" aria-hidden="true" initial={{ opacity: 0.8 }} animate={{ opacity: 0 }} transition={{ duration: 1.6, ease: 'easeOut' }} />
      <AnimatePresence mode="wait">
      {intro ? (
        <motion.section key="intro" className="intro-screen" aria-labelledby="intro-title" variants={screenVariants} initial="hidden" animate="show" exit="exit">
          <div className="intro-model">{dialog !== 'map' && <VillageCanvas selectedId="01" chapterTitles={chapterLabels} onSelect={(id) => { const target = locations.find((item) => item.id.endsWith(id) || item.id === id); if (target) guardedGoTo(target.id); }} />}</div>
          <div className="intro-copy">
            <p className="eyebrow"><span className="eyebrow-dash" /><InkFade delay={0.2}>二十年前，旧客簿上多出一个名字</InkFade></p>
            <h1 id="intro-title" aria-label="归灯，先看灯下的影子。"><InkChars text="归灯" /><span className="intro-title-comma" aria-hidden="true">，</span><br /><em><InkFade delay={0.9}>先看灯下的影子。</InkFade></em></h1>
            <p className="intro-lead">一封没有邮戳的信，把沈归带回连云老街。<br />天亮以前，他要从一册被水泡开的客簿里，<br />分清谁的名字被写错，谁又在巷口等他回家。</p>
            <div className="intro-meta"><span>单人叙事体验</span><i />12 个剧情站点<i /><span>支持静音游玩</span></div>
            {story.chapters[0]?.introQuote && <p className="letter-quote">{story.chapters[0].introQuote}</p>}
            <div className="intro-actions">
              <button className="primary-button" type="button" onClick={() => void startJourney()}><span className="button-lamp" />举灯入巷 <span className="button-arrow">↗</span></button>
              <button className="quiet-button" type="button" onClick={() => setDialog('map')}>先看街区模型 <span>⌁</span></button>
            </div>
            <div className="disclaimer">故事为虚构创作；现实地理与资料来源可随时查阅。</div>
          </div>
          <div className="model-hint"><span>拖动旋转</span><i /><span>滚轮缩放模型</span></div>
          <div className="intro-index"><span>夜行档案</span><b>01</b><i>/</i><b>12</b></div>
        </motion.section>
      ) : ending ? (
        <motion.section key="ending" className="ending-screen" variants={screenVariants} initial="hidden" animate="show" exit="exit">
          <div className="ending-orbit" aria-hidden="true"><span>灯</span></div>
          <div className="ending-copy">
            <p className="eyebrow">天亮了 · 客簿最后一页</p>
            <InkTitle text={ending.title} />
            <StoryReader lines={inkLines.length ? inkLines : ending.prose ?? []} page={storyPage} onPageChange={setStoryPage} />
            <div className="ending-actions"><button className="primary-button" type="button" onClick={newGame}>重新走一遍 <span className="button-arrow">↺</span></button><button className="quiet-button" type="button" onClick={() => setDialog('journal')}>查看手记 <span>▤</span></button></div>
            <p className="ending-note">这段故事的声音会在此处停下。</p>
          </div>
        </motion.section>
      ) : chapter ? (
        <motion.section key={chapter.id} className="chapter-screen" aria-labelledby="chapter-title" variants={screenVariants} initial="hidden" animate="show" exit="exit">
          <ChapterRail chapters={locations} currentIndex={pageIndex} visited={visited} onNavigate={guardedGoTo} onOpenMap={() => setDialog('map')} />

          <div className="chapter-copy">
            <div className="chapter-meta"><span className="chapter-number">{String(pageIndex + 1).padStart(2, '0')}</span><span className="meta-rule" /><span>{chapter.weather ?? '雾气压低，雨没有停'}</span><span className="meta-dot">·</span><span>{chapter.lantern ?? '灯火在雾中偏向一侧'}</span></div>
            <InkTitle id="chapter-title" text={chapter.title.replace(/^\d+[｜|.、]\s*/, '')} />
            <p className="chapter-subtitle"><InkFade delay={0.55}>{chapter.subtitle}</InkFade></p>
            <StoryReader lines={inkLines.length ? inkLines : chapter.prose} page={storyPage} onPageChange={setStoryPage} ariaLive />
            {branchEcho && <p className="branch-echo">旧路留下的痕迹：{branchEcho}</p>}
            {inkChoices.length > 1 && pageIndex !== locations.length - 1 && <div className="choice-deck" aria-label="选择行动">{inkChoices.map((option) => <button key={`${option.index}-${option.text}`} type="button" onClick={() => chooseInkAction(option.index)}>{option.text.replace(/^\[|\]$/g, '')}<span>↗</span></button>)}</div>}
            {chapter.npc && <div className="npc-line"><span className="npc-mark">说</span><span>{chapter.npc}</span></div>}
            <figure className="prop-object"><img src={`/assets/props/${PROP_FILES[pageIndex]}.webp`} alt={`${chapter.clue.title}相关线索物件`} /><figcaption><i>现场物证</i><span>{chapter.clue.title}</span></figcaption></figure>
            <div className="interaction-card">
              <div className="interaction-icon">{pageIndex === 2 ? '♧' : pageIndex === 6 ? '◉' : pageIndex === 10 ? '◌' : '⌕'}</div>
              <div className="interaction-content"><span className="interaction-kicker">可调查线索</span><b>{chapter.interaction.label}</b><small>{chapter.interaction.detail}</small></div>
              {pageIndex === 10 && !puzzleSolved ? (
              <div className="puzzle-input"><input aria-label={chapter.puzzle?.prompt ?? '填写核名答案'} value={answer} onChange={(event) => setAnswer(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') inspectScene(); }} placeholder="输入两个姓名" maxLength={32} /><button type="button" onClick={inspectScene} aria-label="核对姓名">↵</button></div>
              ) : pageIndex === locations.length - 1 ? <span className="choice-prompt">选择结局，写下归路</span> : inkChoices.length > 1 ? <span className="choice-prompt">选择行动后继续</span> : (
                <button className={`inspect-button ${explored.includes(chapter.id) ? 'complete' : ''}`} type="button" onClick={inspectScene} aria-label={explored.includes(chapter.id) ? '线索已记录' : '调查并记录线索'}>{explored.includes(chapter.id) ? '已记录 ✓' : '调查'}<span>↗</span></button>
              )}
            </div>
            <div className="explore-entry">
              <button type="button" className="primary-button explore-button" onClick={() => setExploreOpen(true)} aria-label={`进入${chapter.title.replace(/^\d+[｜|.、]\s*/, '')}的三维现场进行探索`}>
                <span aria-hidden="true">⌖</span> 进入现场探索
              </button>
              <small>在三维现场中点击发光处调查、拾取物品、解开谜题</small>
            </div>
            {choice && <div className="decision-tag">你选择：{choice}</div>}
            {message && <div className="toast-message" role="status">{message}</div>}
            <div className="chapter-footer">
              <div className="source-access"><button type="button" onClick={() => setDialog('sources')}>史料与创作边界 <span>↗</span></button><span>·</span><span>{chapter.sources.length} 条来源</span></div>
              <div className="chapter-nav"><button type="button" disabled={pageIndex === 0} onClick={() => guardedGoTo(locations[pageIndex - 1].id)} aria-label="上一站">←</button><span>{String(pageIndex + 1).padStart(2, '0')} <i>/</i> {String(locations.length).padStart(2, '0')}</span><button type="button" onClick={() => void moveNext()} aria-label={pageIndex === locations.length - 1 ? '查看结局选择' : '下一站'}>{pageIndex === locations.length - 1 ? '✓' : '→'}</button></div>
            </div>
          </div>

          <div className="scene-caption"><span className="caption-line" /><span>{chapter.scene}</span><span className="caption-place">连云老街 · 叙事空间</span></div>
          <div className="sound-cue" aria-hidden="true"><span className={soundOn ? 'wave active' : 'wave'} /><InkFade delay={0.8} className="sound-cue-text">{soundOn ? '雨声停了一拍' : '开启声音，听见巷子'}</InkFade></div>
          <div className="chapter-clue-preview"><span className="clue-preview-mark">簿</span><span><i>手记线索</i><b>{collected.size ? `${collected.size} 项已收` : '尚未找到'}</b></span><button type="button" onClick={() => setDialog('journal')} aria-label="查看已收线索">↗</button></div>
          {pendingSkipId && (
            <div className="skip-confirm" role="alertdialog" aria-labelledby="skip-confirm-title" aria-describedby="skip-confirm-desc">
              <h2 id="skip-confirm-title">核名尚未完成</h2>
              <p id="skip-confirm-desc">第 11 站「旅社账簿」的核名谜题还没有解开。继续前进就会跳过它，账簿合上便不再打开。</p>
              <div className="skip-confirm-actions">
                <button type="button" className="primary-button" onClick={() => setPendingSkipId(null)}>返回解谜 <span className="button-arrow">↩</span></button>
                <button type="button" className="quiet-button" onClick={() => { const target = pendingSkipId; if (target) goTo(target); }}>仍要跳过</button>
              </div>
            </div>
          )}
        </motion.section>
      ) : null}
      </AnimatePresence>

      {dialog && <div className="overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDialog(null); }}>
        <section ref={dialogPanel} className={`overlay-panel ${dialog === 'map' ? 'map-panel' : ''}`} role="dialog" aria-modal="true" aria-label={dialog === 'map' ? '连云老街街区模型' : dialog === 'journal' ? '线索手记' : dialog === 'sources' ? '史料来源与民俗志' : '声音设置'} tabIndex={-1}>
          <header className="panel-header"><div><span className="panel-kicker">{dialog === 'map' ? '街区沙盘' : dialog === 'journal' ? '随身记录' : dialog === 'sources' ? '资料簿' : '声场设置'}</span><h2>{dialog === 'map' ? '沿石街往里走' : dialog === 'journal' ? '沈归的手记' : dialog === 'sources' ? '史料与民俗志' : '把声音留在巷里'}</h2></div><button type="button" className="close-button" onClick={() => setDialog(null)} aria-label="关闭">×</button></header>
          {dialog === 'map' ? <>
            <p className="map-intro">拖动旋转、滚轮缩放。点亮的灯火可直接进入对应章节；地图为叙事路线模型，非实测测绘图。</p>
            <div className="map-canvas"><VillageCanvas selectedId={String(Math.max(1, pageIndex + 1)).padStart(2, '0')} chapterTitles={chapterLabels} onSelect={(id) => { const target = locations.find((item, index) => item.id.endsWith(id) || String(index + 1).padStart(2, '0') === id); if (target) guardedGoTo(target.id); }} /></div>
            <div className="map-legend"><span><i className="legend-gold" />当前所在</span><span><i className="legend-ash" />已到访</span><span><i className="legend-red" />异象线索</span></div>
            <div className="map-locations">{locations.map((item, index) => <button type="button" key={item.id} className={`${currentId === item.id ? 'selected' : ''} ${visited.includes(item.id) ? 'arrived' : ''}`} onClick={() => guardedGoTo(item.id)} aria-label={`第 ${String(index + 1).padStart(2, '0')} 站：${item.title.replace(/^\d+[｜|.、]\s*/, '')}`}><small>{String(index + 1).padStart(2, '0')}</small><span>{item.title.replace(/^\d+[｜|.、]\s*/, '')}</span><i>{visited.includes(item.id) ? '●' : '○'}</i></button>)}</div>
          </> : dialog === 'journal' ? <JournalPanel chapters={locations} collected={collected} /> : dialog === 'sources' ? <SourcesDialog chapter={chapter} /> : <div className="settings-panel"><p>声音在你点击「举灯入巷」后才会播放。重要听声线索也会显示为文字提示。</p><button className="primary-button" type="button" onClick={() => void toggleSound()}>{soundOn ? '关闭环境声' : '开启环境声'}</button><label>环境音量 <input type="range" min="0" max="1" step="0.01" value={volume} onChange={(event) => updateVolume(Number(event.target.value))} /><b>{Math.round(volume * 100)}%</b></label><button className="quiet-button" type="button" onClick={newGame}>清除存档并重新开始</button></div>}
        </section>
      </div>}
      {exploreOpen && chapter && !ending && (
        <ExploreOverlay
          stationId={chapter.id}
          stationIndex={pageIndex}
          title={chapter.title.replace(/^\d+[｜|.、]\s*/, '')}
          inventory={inventory}
          consumedHotspots={consumedHotspotSet}
          solvedPuzzles={solvedPuzzleSet}
          divined={divined}
          onPickup={handlePickup}
          onConsumeHotspot={(id) => setConsumedHotspots((old) => old.includes(id) ? old : [...old, id])}
          onCombine={handleCombine}
          onUseItemOnHotspot={handleUseItemOnHotspot}
          onPuzzleSolved={handlePuzzleSolved}
          onDivined={handleDivined}
          onOpenCodex={() => { setExploreOpen(false); setDialog('sources'); }}
          onClose={() => setExploreOpen(false)}
        />
      )}
      {!intro && !ending && <div className="bottom-hint"><span>J</span> 手记 <i /> <span>M</span> 街区 <i /> <span>← →</span> 前后章节</div>}
      {story.endings?.length && !intro && !ending && pageIndex === locations.length - 1 && <EndingChoices endings={story.endings} onChoose={chooseEnding} />}
      <footer className="footer-mark"><span>归灯</span><i />基于真实地点考据的虚构故事</footer>
    </main>
    </MotionConfig>
  );
}

export default App;
